import { useEffect, useRef, useState } from 'react';

import type { AnalyticsEvents } from '@sunflower-games/analytics-events';
import { logCoralogix } from '@sunflower-games/coralogix-rum';
import classNames from 'classnames';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { DialogContent } from '../../../components/Dialog/DialogContent';
import { DialogHeader } from '../../../components/Dialog/DialogHeader';
import { DialogLoader } from '../../../components/Dialog/DialogLoader';
import { ErrorMessage } from '../../../components/ErrorMessage/ErrorMessage';
import type { WizardChildProps } from '../../../components/Wizard/types';
import { analytics } from '../../../services/analytics';
import { auth } from '../../../services/auth';
import { useStores } from '../../../stores/useStores';
import { getErrorMessage } from '../../../utils/errors';

import styles from './EmailVerification.module.scss';
import envelope from './envelope.png';

export const EmailVerification = observer(({ isCurrent, prevStep, nextStep }: WizardChildProps) => {
  const { authStore, portalStore, rewardsStore } = useStores();

  const { t } = useTranslation();

  const { sendVerificationEmailCooldown } = authStore;

  const [error, setError] = useState<string>('');

  const isProcessing = computed(() => authStore.pending('resendEmailVerification')).get();

  const handleGoBack = async () => {
    const eventParams: AnalyticsEvents['email_account_check_email'] = {
      event_category: 'sign_up',
      event_action: 'click',
      click_location: 'close',
    };
    void analytics.sendEvent('email_account_check_email', eventParams);
    logCoralogix('email_account_check_email', eventParams);
    await auth.signOut();
    prevStep();
  };

  const handleClose = async () => {
    const eventParams: AnalyticsEvents['email_account_check_email'] = {
      event_category: 'sign_up',
      event_action: 'click',
      click_location: 'close',
    };
    void analytics.sendEvent('email_account_check_email', eventParams);
    logCoralogix('email_account_check_email', eventParams);
    await auth.signOut();
    portalStore.closePortal();
  };

  const handleResend = async () => {
    try {
      const eventParams: AnalyticsEvents['resend_email'] = {
        event_category: 'sign_up',
        event_action: 'click',
      };
      void analytics.sendEvent('resend_email', eventParams);
      logCoralogix('resend_email', eventParams);
      await authStore.resendEmailVerification();
    } catch (e) {
      const msg = getErrorMessage(e);
      setError(msg);
    }
  };

  const emailCheckImpressionFiredRef = useRef(false);

  useEffect(() => {
    if (isCurrent) {
      if (!emailCheckImpressionFiredRef.current) {
        emailCheckImpressionFiredRef.current = true;
        void analytics.sendEvent('email_account_check_email', {
          event_category: 'sign_up',
          event_action: 'impression',
        });
      }

      const unsub = authStore.trackEmailVerificationCompletion(() => {
        nextStep();
      });

      return () => unsub();
    } else {
      emailCheckImpressionFiredRef.current = false;
    }
  }, [authStore, nextStep, isCurrent, rewardsStore]);

  useEffect(() => {
    return () => authStore.clearSendVerificationEmailCooldown();
  }, [authStore]);

  return (
    <>
      <DialogHeader
        className={styles.title}
        title={t('auth.checkYourEmail')}
        titleDataTestId="email-verification-title"
        onClose={handleClose}
        onGoBack={handleGoBack}
      />

      <DialogLoader isProcessing={isProcessing} />

      <DialogContent className={styles.content}>
        <div className={styles.messageFirstLine}>{t('emailVerification.verifyYourAccount')}</div>
        <div className={styles.messageSecondLine}>{t('emailVerification.getWelcomeBonus')}</div>

        <img alt="envelope" className={styles.envelopeImage} src={envelope} />

        <p className={styles.sentConfirmationEmail}>
          {t('emailVerification.confirmationEmailSent')} <b>{authStore.currentUserEmail}</b>
        </p>

        <p className={styles.resendRow}>
          {t('emailVerification.notInInbox')}{' '}
          {isProcessing ? (
            <span className={styles.resending}>{t('emailVerification.resending')}</span>
          ) : (
            <>
              <span
                className={classNames('text-link', { [styles.resendBtnDisabled]: sendVerificationEmailCooldown > 0 })}
                onClick={handleResend}
              >
                {t('emailVerification.resend')}
              </span>
              {sendVerificationEmailCooldown > 0 ? ` in ${sendVerificationEmailCooldown}` : ''}
            </>
          )}
        </p>
        <ErrorMessage>{error}</ErrorMessage>
      </DialogContent>
    </>
  );
});
